import { useState, useCallback } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { PRIMARY_NAV } from '@/data/navigation';
import { CONTACT } from '@/lib/constants';
import { LOGOS } from '@/lib/assets';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/cn';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { MobileMenu } from './MobileMenu';
import { NavDropdown } from './NavDropdown';
import { AnimatedHamburger } from './AnimatedHamburger';
import { buttonClasses } from '@/components/ui/buttonStyles';

const GRAY_HEADER_ROUTES: string[] = [
  ROUTES.nossosDiferenciais,
  ROUTES.nossaEssencia,
  ROUTES.comoFunciona,
  ROUTES.privacidade,
  ROUTES.sejaNossoParceiro,
  ROUTES.guiaDireitoTitulares,
  ROUTES.denuncia,
  ROUTES.trabalheConosco,
];

/**
 * Primary site navigation.
 *
 * Visual states:
 * - Transparent with white text over the Home hero (only on "/" + not scrolled).
 * - Gray bg with purple text on select inner pages (before scroll).
 * - Solid white with purple text once scrolled (or on routes not in the gray list).
 *
 * Behavior:
 * - Sticky at top, stays above mosaic cards.
 * - lg+: inline 6 primary links + right-aligned WhatsApp pill.
 * - <lg: logo + hamburger → opens MobileMenu full-screen overlay.
 */
export function Navbar() {
  const { pathname } = useLocation();
  const scrolled = useScrollPosition(40);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Stays true while the overlay is still animating out, so the header keeps
  // its "purple/transparent + lifted" look and the hamburger stays visible
  // through the entire close (ink retract + X → bars morph).
  const [closing, setClosing] = useState(false);

  const navigate = useNavigate();
  const isHome = pathname === ROUTES.home;
  const isGrayRoute = GRAY_HEADER_ROUTES.includes(pathname);
  const transparent = isHome && !scrolled;
  // The menu is visually "present" while open OR mid-close animation.
  const menuActive = mobileOpen || closing;

  const closeMenu = useCallback(() => {
    setMobileOpen(false);
    setClosing(true);
  }, []);
  const toggleMenu = useCallback(() => {
    setMobileOpen((open) => {
      setClosing(open);
      return !open;
    });
  }, []);

  const handleNavClick = useCallback(
    (e: React.MouseEvent, path: string) => {
      const hashIndex = path.indexOf('#');

      if (hashIndex !== -1) {
        const route = path.slice(0, hashIndex) || '/';
        const hash = path.slice(hashIndex + 1);

        if (pathname === route) {
          e.preventDefault();
          document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
        } else {
          e.preventDefault();
          void navigate(route);
          setTimeout(() => {
            document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
        return;
      }

      if (pathname === path) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [pathname, navigate]
  );

  return (
    <>
      {/* Header sits at z-40 — BELOW the mobile overlay (z-50). The purple ink
          floods over it on open and retracts to reveal its real resting color
          on close (no white flash). Its bg/text never change while the menu is
          active; only the floating toggle below tracks the open state. */}
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-40 transition-colors duration-300',
          transparent
            ? 'bg-transparent text-white'
            : isGrayRoute && !scrolled
              ? 'bg-brand-lightgray text-brand-purple'
              : 'bg-brand-bg/95 backdrop-blur-sm text-brand-purple border-b border-brand-lightgray/50'
        )}
        data-navbar-variant={transparent ? 'transparent' : isGrayRoute && !scrolled ? 'gray' : 'solid'}
      >
        <nav
          aria-label="Navegação principal"
          className="container-page flex items-center justify-between"
        >
          <Link
            to={ROUTES.home}
            aria-label="ARCOLL TECH — Ir para a Home"
          >
            <img
              src={LOGOS.icon}
              alt=""
              width={48}
              height={48}
              className={cn(
                'h-20 md:h-24 lg:h-28 w-auto transition-[filter] duration-300',
                !transparent && 'navbar-icon-purple'
              )}
            />
          </Link>

          <ul className="hidden lg:flex items-center gap-7">
            {PRIMARY_NAV.map((item) => {
              if (item.children) {
                return (
                  <NavDropdown
                    key={item.label}
                    item={item}
                    transparent={transparent}
                    onItemClick={handleNavClick}
                  />
                );
              }

              const path = item.path ?? ROUTES.home;
              const isHash = path.includes('#');
              return (
                <li key={path}>
                  <NavLink
                    to={path}
                    end={path === ROUTES.home}
                    onClick={(e) => handleNavClick(e, path)}
                    className={({ isActive }) =>
                      cn(
                        'text-sm transition-colors',
                        transparent ? 'hover:text-brand-yellow' : 'hover:text-brand-yellow-text',
                        isActive && !isHash
                          ? transparent
                            ? 'text-brand-yellow'
                            : 'text-brand-yellow-text'
                          : transparent
                            ? 'text-white/90'
                            : 'text-brand-purple'
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center gap-2">
            <a
              href={CONTACT.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'hidden md:inline-flex mr-10 md:mr-0',
                buttonClasses('primary-yellow', 'sm', 'text-xxs! md:text-xs!')
              )}
            >
              Atendimento pelo Whatsapp
            </a>
            {/* Reserves the toggle's slot so the WhatsApp pill never shifts —
                the real (morphing) button floats above everything below. */}
            <span aria-hidden="true" className="inline-flex lg:hidden h-10 w-10" />
          </div>
        </nav>
      </header>

      {/* Floating hamburger ↔ X toggle. Lives above the overlay (z-60) so it
          stays visible and keeps morphing through the entire open/close ink
          animation. Aligned to the header's reserved slot via container-page. */}
      <div className="pointer-events-none fixed inset-x-0 top-0 z-60 lg:hidden">
        <div className="container-page flex h-20 md:h-24 items-center justify-end">
          <button
            type="button"
            onClick={toggleMenu}
            aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileOpen}
            className={cn(
              'pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-yellow',
              transparent || menuActive
                ? 'text-white hover:bg-white/10'
                : 'text-brand-purple hover:bg-brand-lightgray/60'
            )}
          >
            <AnimatedHamburger open={mobileOpen} />
          </button>
        </div>
      </div>

      <MobileMenu
        open={mobileOpen}
        onClose={closeMenu}
        onExitComplete={() => setClosing(false)}
      />

      {/* Spacer — pushes page content below the fixed navbar except on Home
          where the hero covers it visually. */}
      {!isHome && <div aria-hidden="true" className="h-[72px] lg:h-[64px]" />}
    </>
  );
}
